<script lang="ts" setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { DateTime } from 'luxon'
import type { GeoFenceVisitAPIResponse } from '~/services/apiClientGps'
import {
  GeoFencesApi as GeoFencesApiClass,
  PingsApi as PingsApiClass,
} from '~/services/apiClientGps'
import { parseWKTPolygon } from '~/utils/wkt'

// Types
interface MapPing {
  longitude: number
  latitude: number
  time?: string
  recorded_speed_mph?: number
}

interface Geofence {
  name: string
  polygon: any // Using any since the API returns a complex polygon object
}

// Props and emits
const props = defineProps<{
  isFullScreen?: boolean
  modelValue: 'streets' | 'satellite'
  selectedVisit?: GeoFenceVisitAPIResponse | null
  loading?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: 'streets' | 'satellite'): void
  (e: 'update:loading', value: boolean): void
}>()

// Constants
const MAP_STYLES = {
  streets: 'mapbox://styles/mapbox/streets-v12',
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
} as const

const DEFAULT_CENTER = [-118.2095, 33.7701] as [number, number]
const DEFAULT_ZOOM = 12

const SPEED_THRESHOLDS = {
  HIGH: 35,
  MEDIUM: 20,
  LOW: 10,
} as const

const SPEED_COLORS = {
  GREEN: '#22c55e',
  YELLOW: '#eab308',
  ORANGE: '#f97316',
  RED: '#ef4444',
} as const

// State
const map = ref<mapboxgl.Map | null>(null)
const mapContainer = ref<HTMLElement | null>(null)
const popup = ref<mapboxgl.Popup | null>(null)
const pings = ref<MapPing[]>([])
const geofences = ref<Geofence[]>([])

// API instances
const pingsApi = new PingsApiClass()
const geofencesApi = new GeoFencesApiClass()

// Utility functions
const formatPingTime = (time?: string): string => {
  if (!time) return 'Time not available'
  try {
    const dt = DateTime.fromISO(time)
    return dt.isValid
      ? dt.toLocaleString(DateTime.DATETIME_FULL)
      : 'Time not available'
  } catch {
    return 'Time not available'
  }
}

const formatSpeed = (speed?: number): string => {
  return typeof speed === 'number'
    ? `${speed.toFixed(1)} mph`
    : 'Speed not available'
}

// Popup handling
const initializePopup = () => {
  if (!map.value) return

  popup.value = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false,
    className: 'route-popup',
  })

  // Use the invisible point layer for efficient spatial queries
  map.value.on('mousemove', 'route-points', (e) => {
    if (!e.lngLat || !popup.value || !map.value) return

    // Use Mapbox's built-in spatial indexing to get features under the mouse
    const features = map.value.queryRenderedFeatures(e.point, {
      layers: ['route-points'],
    })

    if (features.length === 0) return

    // Get ping data directly from the closest feature properties
    const feature = features[0]
    const pingData = feature.properties

    if (!pingData) return

    const popupContent = `
      <div class="popup-content">
        <div class="popup-row">
          <span class="popup-label">Time:</span>
          <span class="popup-value">${formatPingTime(pingData.time)}</span>
        </div>
        <div class="popup-row">
          <span class="popup-label">Speed:</span>
          <span class="popup-value">${formatSpeed(pingData.recorded_speed_mph)}</span>
        </div>
      </div>
    `

    popup.value
      .setLngLat(e.lngLat)
      .setHTML(popupContent)
      // @ts-expect-error TS2589
      .addTo(map.value as mapboxgl.Map)
  })

  map.value.on('mouseenter', 'route-points', () => {
    if (map.value) map.value.getCanvas().style.cursor = 'pointer'
  })

  map.value.on('mouseleave', 'route-points', () => {
    if (map.value) map.value.getCanvas().style.cursor = ''
    popup.value?.remove()
  })

  // Add hover effects for route segments
  map.value.on('mouseenter', 'route-segments', () => {
    if (map.value) map.value.getCanvas().style.cursor = 'pointer'
  })

  map.value.on('mouseleave', 'route-segments', () => {
    if (map.value) map.value.getCanvas().style.cursor = ''
    popup.value?.remove()
  })
}

// Map layer handling
const showGeofencesOnMap = () => {
  if (!map.value || !geofences.value.length) {
    // If no geofences to show, still attempt to clean up any existing ones
    if (map.value) {
      const existingMapLayers = map.value.getStyle().layers || []
      const geofenceSourceIdsToRemove = new Set<string>()

      existingMapLayers.forEach((layer) => {
        if (layer.id.startsWith('geofence-')) {
          map.value?.removeLayer(layer.id)
          // Attempt to derive source ID. Assumes format like 'geofence-INDEX-suffix' or 'geofence-INDEX'
          const parts = layer.id.split('-')
          if (parts.length >= 2) {
            const potentialSourceId = `${parts[0]}-${parts[1]}`
            if (map.value?.getSource(potentialSourceId)) {
              geofenceSourceIdsToRemove.add(potentialSourceId)
            }
          }
        }
      })
      geofenceSourceIdsToRemove.forEach((sourceId) =>
        map.value?.removeSource(sourceId)
      )
    }
    return
  }

  // Remove existing geofence layers and sources more carefully
  const style = map.value.getStyle()
  const layersToRemove: string[] = []
  const sourcesToRemove = new Set<string>()

  if (style && style.layers) {
    style.layers.forEach((layer) => {
      if (layer.id.startsWith('geofence-')) {
        layersToRemove.push(layer.id)
        // Assuming source is named like 'geofence-INDEX'
        const parts = layer.id.split('-')
        if (parts.length >= 2) {
          // e.g., geofence-0-fill -> geofence-0
          sourcesToRemove.add(`${parts[0]}-${parts[1]}`)
        }
      }
    })
  }

  layersToRemove.forEach((layerId) => {
    if (map.value?.getLayer(layerId)) {
      map.value.removeLayer(layerId)
    }
  })

  sourcesToRemove.forEach((sourceId) => {
    if (map.value?.getSource(sourceId)) {
      map.value.removeSource(sourceId)
    }
  })

  // Add each geofence
  geofences.value.forEach((fence, index) => {
    const coordinates = parseWKTPolygon(fence.polygon)
    if (!coordinates.length) {
      console.error('No valid coordinates found for geofence:', fence)
      return
    }

    const sourceId = `geofence-${index}`

    // Add geofence polygon
    map.value?.addSource(sourceId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {
          name: fence.name,
        },
        geometry: {
          type: 'Polygon',
          coordinates,
        },
      },
    })

    // Add fill layer
    map.value?.addLayer({
      id: `${sourceId}-fill`,
      type: 'fill',
      source: sourceId,
      paint: {
        'fill-color': '#088',
        'fill-opacity': 0.2,
        'fill-outline-color': '#ff3b30',
      },
    })

    // Add label
    map.value?.addLayer({
      id: `${sourceId}-label`,
      type: 'symbol',
      source: sourceId,
      layout: {
        'text-field': ['get', 'name'],
        'text-size': 14,
        'text-justify': 'center',
        'text-anchor': 'center',
      },
      paint: {
        'text-color': '#1e293b',
        'text-halo-color': '#ffffff',
        'text-halo-width': 2,
      },
    })
  })
}

const showRouteOnMap = () => {
  if (!map.value || !pings.value.length) return

  // Remove existing route layers and sources
  const style = map.value.getStyle()
  const layersToRemove: string[] = []
  const sourcesToRemove = new Set<string>()

  if (style && style.layers) {
    style.layers.forEach((layer) => {
      if (
        layer.id.startsWith('route-') ||
        layer.id.startsWith('start-') ||
        layer.id.startsWith('end-')
      ) {
        layersToRemove.push(layer.id)
        // Add sources to remove set
        if (layer.source && typeof layer.source === 'string') {
          sourcesToRemove.add(layer.source)
        }
      }
    })
  }

  layersToRemove.forEach((layerId) => {
    if (map.value?.getLayer(layerId)) {
      map.value.removeLayer(layerId)
    }
  })

  sourcesToRemove.forEach((sourceId) => {
    if (map.value?.getSource(sourceId)) {
      map.value.removeSource(sourceId)
    }
  })

  const coordinates = pings.value.map((ping) => [ping.longitude, ping.latitude])

  // Create speed-based route segments as a single GeoJSON source
  const routeSegments: any[] = []
  for (let i = 0; i < pings.value.length - 1; i++) {
    const currentPing = pings.value[i]
    const nextPing = pings.value[i + 1]

    // Create segment from current ping to next ping
    const segmentCoordinates = [
      [currentPing.longitude, currentPing.latitude],
      [nextPing.longitude, nextPing.latitude],
    ]

    // Use the destination ping's speed for the segment
    const speed = nextPing.recorded_speed_mph || 0

    routeSegments.push({
      type: 'Feature' as const,
      properties: {
        speed,
        segmentIndex: i,
      },
      geometry: {
        type: 'LineString' as const,
        coordinates: segmentCoordinates,
      },
    })
  }

  // Add single route segments source
  map.value.addSource('route-segments', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: routeSegments,
    },
  })

  // Add route segments layer with data-driven styling
  map.value.addLayer({
    id: 'route-segments',
    type: 'line',
    source: 'route-segments',
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-color': [
        'case',
        ['>', ['get', 'speed'], SPEED_THRESHOLDS.HIGH],
        SPEED_COLORS.GREEN, // Green for >35 MPH
        ['>=', ['get', 'speed'], SPEED_THRESHOLDS.MEDIUM],
        SPEED_COLORS.YELLOW, // Yellow for 20-35 MPH
        ['>=', ['get', 'speed'], SPEED_THRESHOLDS.LOW],
        SPEED_COLORS.ORANGE, // Orange for 10-20 MPH
        SPEED_COLORS.RED, // Red for <10 MPH
      ],
      'line-width': 3,
    },
  })

  // Create point features for efficient spatial queries
  const routePointsSource = {
    type: 'geojson' as const,
    data: {
      type: 'FeatureCollection' as const,
      features: pings.value.map((ping, index) => ({
        type: 'Feature' as const,
        properties: {
          time: ping.time,
          recorded_speed_mph: ping.recorded_speed_mph,
          index,
        },
        geometry: {
          type: 'Point' as const,
          coordinates: [ping.longitude, ping.latitude],
        },
      })),
    },
  }

  map.value.addSource('route-points', routePointsSource)

  // Add invisible point layer for efficient queries (no visual styling)
  map.value.addLayer({
    id: 'route-points',
    type: 'circle',
    source: 'route-points',
    paint: {
      'circle-radius': 8, // Larger radius for easier hover detection
      'circle-opacity': 0, // Invisible
    },
  })

  // Add start and end points with improved styling
  const pointStyle = {
    'circle-radius': 6,
    'circle-stroke-width': 2,
    'circle-stroke-color': '#ffffff',
    'circle-stroke-opacity': 0.9,
  }

  // Start point (most recent ping)
  map.value.addSource('start-point', {
    type: 'geojson',
    data: {
      type: 'Feature',
      properties: {
        time: formatPingTime(pings.value[pings.value.length - 1].time),
      },
      geometry: {
        type: 'Point',
        coordinates: coordinates[coordinates.length - 1],
      },
    },
  })

  map.value.addLayer({
    id: 'start-point',
    type: 'circle',
    source: 'start-point',
    paint: {
      ...pointStyle,
      'circle-color': '#22c55e',
      'circle-opacity': 0.9,
    },
  })

  // Start point label
  map.value.addLayer({
    id: 'start-label',
    type: 'symbol',
    source: 'start-point',
    layout: {
      'text-field': 'Start',
      'text-size': 12,
      'text-anchor': 'top',
      'text-offset': [0, 0.8],
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: {
      'text-color': '#166534',
      'text-halo-color': '#ffffff',
      'text-halo-width': 2,
    },
  })

  // End/Current point (oldest ping)
  map.value.addSource('end-point', {
    type: 'geojson',
    data: {
      type: 'Feature',
      properties: {
        time: formatPingTime(pings.value[0].time),
      },
      geometry: {
        type: 'Point',
        coordinates: coordinates[0],
      },
    },
  })

  map.value.addLayer({
    id: 'end-point',
    type: 'circle',
    source: 'end-point',
    paint: {
      ...pointStyle,
      'circle-color': props.selectedVisit?.departure ? '#ef4444' : '#2563eb',
      'circle-opacity': 0.9,
    },
  })

  // End point label
  map.value.addLayer({
    id: 'end-label',
    type: 'symbol',
    source: 'end-point',
    layout: {
      'text-field': props.selectedVisit?.departure ? 'End' : 'Current',
      'text-size': 12,
      'text-anchor': 'top',
      'text-offset': [0, 0.8],
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: {
      'text-color': props.selectedVisit?.departure ? '#991b1b' : '#1e40af',
      'text-halo-color': '#ffffff',
      'text-halo-width': 2,
    },
  })

  // Fit map to route bounds
  const bounds = new mapboxgl.LngLatBounds()
  coordinates.forEach((coord) => bounds.extend(coord as [number, number]))
  map.value.fitBounds(bounds, {
    padding: { top: 50, bottom: 50, left: 50, right: 50 },
    duration: 1000,
    maxZoom: 16,
  })

  initializePopup()
}

// Data loading
const loadVisitPings = async () => {
  if (
    !props.selectedVisit?.tracking_device.id ||
    !props.selectedVisit?.arrival
  ) {
    pings.value = [] // Clear pings if no visit/device/arrival
    if (map.value) showRouteOnMap() // Attempt to clear route from map
    return
  }

  emit('update:loading', true)
  try {
    const response = await pingsApi.getPingsByDevicePingsByDeviceDeviceIdGet(
      props.selectedVisit.tracking_device.id,
      props.selectedVisit.arrival,
      // If no departure time (in-progress), use current time
      props.selectedVisit.departure || DateTime.now().toISO()
    )
    pings.value = response.data
    if (map.value) {
      showRouteOnMap()
    }
  } catch (error) {
    console.error('Error loading pings:', error)
    pings.value = [] // Clear pings on error
    if (map.value) showRouteOnMap() // Attempt to clear route from map
  } finally {
    emit('update:loading', false)
  }
}

const loadGeofences = async () => {
  try {
    const response = await geofencesApi.getAllGeoFencesGeoFencesGet()
    geofences.value = response.data
    if (map.value) {
      showGeofencesOnMap()
    }
  } catch (error) {
    console.error('Error loading geofences:', error)
    geofences.value = [] // Clear geofences on error
  }
}

// Map initialization and cleanup
const cleanupMap = () => {
  if (map.value) {
    map.value.remove()
    map.value = null
  }
  // Reset pings and geofences when map is cleaned up to avoid stale data on re-init
  pings.value = []
  geofences.value = []
}

const initMap = async () => {
  if (!mapContainer.value || map.value) {
    return
  }

  await nextTick() // Ensure DOM is ready

  if (!mapContainer.value) {
    console.error('Map container is not available after nextTick')
    return
  }

  const containerId = props.isFullScreen
    ? 'fullscreen-map-container'
    : 'regular-map-container'

  // Ensure the container element exists if we are dynamically setting IDs
  mapContainer.value.id = containerId

  // Ensure the container is empty before initializing the map
  mapContainer.value.innerHTML = ''

  const localMapInstance = new mapboxgl.Map({
    container: containerId, // Use the ID directly
    style: MAP_STYLES[props.modelValue],
    center: DEFAULT_CENTER,
    zoom: DEFAULT_ZOOM,
  })

  // Using 'as any' on addControl method itself due to persistent Mapbox GL type inference issues.
  // Operations on localMapInstance
  ;(localMapInstance.addControl as any)(
    new mapboxgl.NavigationControl(),
    'top-right'
  )

  localMapInstance.on('load', () => {
    loadGeofences()
    if (props.selectedVisit) {
      loadVisitPings()
    }
  })

  map.value = localMapInstance
}

// Style handling
const updateMapStyle = async (style: 'streets' | 'satellite') => {
  if (!map.value) return

  const currentPings = [...pings.value]
  const currentGeofences = [...geofences.value]

  map.value.setStyle(MAP_STYLES[style])
  map.value.once('style.load', () => {
    if (!map.value) return
    if (currentGeofences.length) {
      geofences.value = currentGeofences
      showGeofencesOnMap()
    }
    if (currentPings.length) {
      pings.value = currentPings
      showRouteOnMap()
    }
  })
}

// Watchers
watch(() => props.modelValue, updateMapStyle)

watch(
  () => props.selectedVisit,
  async (newVisit, oldVisit) => {
    if (!newVisit) {
      cleanupMap() // Visit deselected, remove map and data
      return
    }
    // If a new visit is selected (and it's different from the old one or map doesn't exist)
    if (newVisit !== oldVisit || !map.value) {
      if (!map.value && mapContainer.value) {
        // Map doesn't exist, but container does (e.g. visit selected for the first time, or after cleanup)
        await initMap()
      } else if (map.value) {
        // Map exists, just load new pings for the new visit
        await loadVisitPings()
      }
    }
  }
)

watch(mapContainer, async (newContainer) => {
  if (newContainer && !map.value) {
    // Container is ready and map not yet initialized
    await initMap()
  }
})

// Lifecycle
onMounted(async () => {
  mapboxgl.accessToken =
    'pk.eyJ1Ijoib21pZHJvc2hhbmllIiwiYSI6ImNtOGxpaXkwNjBrMWkyaXF1dWtvYmh2b2kifQ.1V8b-WsQ6qkqveYjXgCvdg'
  if (mapContainer.value && !map.value) {
    await initMap()
  }
})

onBeforeUnmount(() => {
  cleanupMap() // Ensure map is cleaned up when component instance is destroyed
})
</script>

<template>
  <div
    v-loading="props.loading"
    class="w-full h-full bg-gray-100 rounded-lg overflow-hidden"
    :class="{ 'h-[350px]': !isFullScreen }"
    element-loading-background="rgba(255, 255, 255, 0.8)"
  >
    <div ref="mapContainer" class="w-full h-full" />
  </div>
</template>

<style scoped lang="scss">
.mapboxgl-canvas {
  outline: none;
}

.route-popup {
  @apply bg-white rounded-lg shadow-lg p-2;
}

.popup-content {
  @apply space-y-1;
}

.popup-row {
  @apply flex items-center justify-between gap-4;
}

.popup-label {
  @apply text-gray-500 text-sm;
}

.popup-value {
  @apply text-gray-900 text-sm font-medium;
}
</style>
